import { useEffect, useState } from "react";
import { Megaphone, Plus, Trash, X, ImageSquare } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useDialog } from "../components/Dialog";

const TAGS = ["NOTICE", "EVENT", "POLICY", "URGENT"];

export default function Announcements() {
  const { confirm } = useDialog();
  const [items, setItems] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    try { const { data } = await api.get("/api/announcements?manage=true"); setItems(data.items || []); }
    catch { setItems([]); }
  }
  useEffect(() => { load(); }, []);

  async function toggle(a, active) {
    setItems((arr) => arr.map((x) => (x.id === a.id ? { ...x, active } : x)));
    try { await api.patch(`/api/announcements/${a.id}`, { active }); } catch { load(); }
  }
  async function remove(a) {
    if (!(await confirm({ title: "Delete announcement?", message: "This announcement will be permanently removed.", confirmText: "Delete", danger: true }))) return;
    setItems((arr) => arr.filter((x) => x.id !== a.id));
    try { await api.delete(`/api/announcements/${a.id}`); } catch { load(); }
  }

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-[var(--fg-soft)]">Post notices for the society. Active ones show on every resident's home screen.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-neon-cyan px-4 py-2.5 text-sm font-bold text-black shadow-glow-cyan transition hover:brightness-110">
          <Plus size={18} weight="bold" /> New announcement
        </button>
      </div>

      {items === null ? (
        <div className="py-20 text-center text-[var(--fg-dim)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--line-strong)] py-20 text-center text-[var(--fg-soft)]">
          <Megaphone size={32} className="mx-auto mb-2 opacity-50" /> No announcements yet
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((a) => (
            <article key={a.id} className={`rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 transition ${a.active ? "" : "opacity-60"}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-neon-cyan/12 px-2.5 py-1 text-[10px] font-extrabold tracking-wide text-neon-cyan">
                  <Megaphone size={11} weight="fill" /> {(a.tag || "NOTICE").toUpperCase()}
                </span>
                <span className={`text-[10px] font-extrabold ${a.active ? "text-neon-lime" : "text-[var(--fg-dim)]"}`}>{a.active ? "ACTIVE" : "INACTIVE"}</span>
                <div className="flex-1" />
                <label className="inline-flex cursor-pointer items-center">
                  <input type="checkbox" className="peer sr-only" checked={!!a.active} onChange={(e) => toggle(a, e.target.checked)} />
                  <span className="relative h-5 w-9 rounded-full bg-[var(--line-strong)] transition peer-checked:bg-neon-lime after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-4" />
                </label>
              </div>
              <h3 className="text-base font-bold text-[var(--fg)]">{a.title}</h3>
              {a.body && <p className="mt-1 text-sm text-[var(--fg-soft)]">{a.body}</p>}
              {Array.isArray(a.images) && a.images.filter((i) => i.url).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {a.images.filter((i) => i.url).map((im, idx) => (
                    <a key={idx} href={im.url} target="_blank" rel="noreferrer">
                      <img src={im.url} alt="" className="h-20 w-20 rounded-lg border border-[var(--line)] object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center text-[11px] text-[var(--fg-dim)]">
                {a.createdByName && <span>By {a.createdByName}</span>}
                <div className="flex-1" />
                <button onClick={() => remove(a)} className="inline-flex items-center gap-1 font-bold text-neon-pink hover:underline">
                  <Trash size={13} /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tag, setTag] = useState("NOTICE");
  const [files, setFiles] = useState([]); // {file, url}
  const [busy, setBusy] = useState(false);

  function addFiles(e) {
    const picked = Array.from(e.target.files || []);
    setFiles((f) => [...f, ...picked.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
    e.target.value = "";
  }

  async function publish() {
    if (title.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/announcements", { title: title.trim(), body: body.trim(), tag });
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("file", files[i].file, files[i].file.name || `image-${i + 1}.jpg`);
        try { await api.post(`/api/announcements/${data.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch {}
      }
      onCreated();
    } catch {} finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-[var(--line)] bg-[var(--shell)] p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-[var(--fg)]">New announcement</h2>
          <button onClick={onClose} className="text-[var(--fg-dim)] hover:text-[var(--fg)]"><X size={22} /></button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {TAGS.map((t) => (
              <button key={t} onClick={() => setTag(t)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition ${tag === t ? "bg-neon-cyan/15 text-neon-cyan ring-1 ring-neon-cyan/50" : "bg-[var(--surface)] text-[var(--fg-soft)] ring-1 ring-[var(--line)]"}`}>{t}</button>
            ))}
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Water supply interruption)"
            className="w-full rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-4 py-3 text-[var(--fg)] outline-none focus:ring-1 focus:ring-neon-cyan" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Details for residents" rows={4}
            className="w-full rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-4 py-3 text-[var(--fg)] outline-none focus:ring-1 focus:ring-neon-cyan" />

          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, idx) => (
                <div key={idx} className="relative">
                  <img src={f.url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                  <button onClick={() => setFiles((arr) => arr.filter((_, j) => j !== idx))}
                    className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-black/70 text-white"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-4 py-2.5 text-sm font-semibold text-neon-cyan">
            <ImageSquare size={18} /> Add images
            <input type="file" accept="image/*" multiple className="hidden" onChange={addFiles} />
          </label>

          <button onClick={publish} disabled={busy || title.trim().length < 3}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-neon-cyan px-4 py-3 text-sm font-bold text-black shadow-glow-cyan transition hover:brightness-110 disabled:opacity-50">
            <Megaphone size={18} weight="fill" /> {busy ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
